import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BadgeCheck,
  Banknote,
  Check,
  Copy,
  GitBranch,
  Plus,
  Receipt,
  Scissors,
  Search,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { Dialog } from '../reusable';
import { useUIColors } from '../../hooks/useTheme';
import {
  canRequestPoApproval,
  getPoInvoicingStatusPresentation,
  getPoPaymentStatusPresentation,
  getPoReceptionStatusPresentation,
  getPoWorkflowStatusPresentation,
  isPendingPoApprovalStatus,
  isPoAwaitingPaymentStatus,
  PO_INVOICING_STATUS_ORDER,
  PO_PAYMENT_STATUS_ORDER,
  PO_RECEPTION_STATUS_ORDER,
  PO_STATUS_ORDER,
  PO_WORKFLOW_STATUS_ORDER,
  resolvePoLifecycleStatuses,
} from '../placeholders/purchaseOrderSummary';

const formatMoney = (value, currency = 'CLP') => {
  const raw = String(currency || 'CLP').trim().toUpperCase() || 'CLP';
  const code = /^[A-Z]{3}$/.test(raw) ? raw : 'CLP';
  try {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: code,
      maximumFractionDigits: code === 'CLP' ? 0 : 2,
    }).format(Number(value || 0));
  } catch {
    return `${code} ${Number(value || 0).toLocaleString('es-CL')}`;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(raw);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Stacked = ({ top, bottom, topColor, mutedColor, topWeight = 700 }) => (
  <div style={{ lineHeight: 1.25 }}>
    <div style={{ fontWeight: topWeight, fontSize: '12px', color: topColor || '#0f172a' }}>{top || '—'}</div>
    {bottom ? (
      <div style={{ fontSize: '11px', color: mutedColor || '#64748b', marginTop: '1px' }}>{bottom}</div>
    ) : null}
  </div>
);

const hexToRgba = (hex, alpha) => {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return `rgba(100, 116, 139, ${alpha})`;
  const n = Number.parseInt(raw, 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const documentTypePresentation = (typeRaw, isDarkMode) => {
  const label = String(typeRaw || '').trim();
  if (!label) return null;
  const s = label.toLowerCase();
  let color = '#64748b';
  if (s.includes('honorario')) color = '#d97706';
  else if (s.includes('exent')) color = '#0d9488';
  else if (s.includes('guia') || s.includes('guía')) color = '#0891b2';
  else if (s.includes('boleta')) color = '#7c3aed';
  else if (s.includes('factura') || s.includes('iva') || s.includes('afecta')) color = '#2563eb';
  return {
    label,
    color,
    bg: hexToRgba(color, isDarkMode ? 0.22 : 0.12),
    border: hexToRgba(color, isDarkMode ? 0.55 : 0.35),
  };
};

const statusPresentation = (statusRaw, isDarkMode) => getPoWorkflowStatusPresentation(statusRaw, isDarkMode);

const StatusBadge = ({ presentation }) => (
  <span
    style={{
      display: 'inline-flex',
      padding: '2px 8px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 600,
      background: presentation.bg,
      color: presentation.color,
      border: `1px solid ${presentation.border}`,
      whiteSpace: 'nowrap',
    }}
  >
    {presentation.label}
  </span>
);

const TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'po_code' },
  { key: 'Fecha', align: 'left', sortKey: 'order_date' },
  { key: 'Proveedor', align: 'left', sortKey: 'supplier_name' },
  { key: 'Contacto', align: 'left', sortKey: 'supplier_contact_name' },
  { key: 'Tipo dcto.', align: 'left', sortKey: 'document_type' },
  { key: 'Cond. pago', align: 'left', sortKey: 'payment_terms' },
  { key: 'Monto neto', align: 'right', sortKey: 'subtotal' },
  { key: 'IVA', align: 'right', sortKey: 'tax_amount' },
  { key: 'Descuentos', align: 'right', sortKey: 'discounts' },
  { key: 'Total a pagar', align: 'right', sortKey: 'amount_due' },
  { key: 'Estado', align: 'center', sortKey: 'workflow_status' },
  { key: 'Facturación', align: 'center', sortKey: 'invoicing_status' },
  { key: 'Recepción', align: 'center', sortKey: 'reception_status' },
  { key: 'Pago', align: 'center', sortKey: 'payment_status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'po_code':
      return String(row.po_code || '').toLowerCase();
    case 'order_date':
      return row.order_date ? new Date(row.order_date).getTime() : 0;
    case 'supplier_name':
      return String(row.supplier_name || '').toLowerCase();
    case 'supplier_contact_name':
      return String(row.supplier_contact_name || '').toLowerCase();
    case 'document_type':
      return String(row.document_type || '').toLowerCase();
    case 'payment_terms':
      return String(row.payment_terms || '').toLowerCase();
    case 'subtotal':
      return Number(row.subtotal || 0);
    case 'tax_amount':
      return Number(row.tax_amount || 0);
    case 'discounts':
      return Number(row.advance_amount || 0) + Number(row.withholdings_amount || 0);
    case 'amount_due': {
      if (row.amount_due != null) return Number(row.amount_due || 0);
      return (
        Number(row.total_amount || 0) -
        Number(row.advance_amount || 0) -
        Number(row.withholdings_amount || 0)
      );
    }
    case 'workflow_status':
      return String(row.workflow_status || '').toLowerCase();
    case 'invoicing_status':
      return String(row.invoicing_status || '').toLowerCase();
    case 'reception_status':
      return String(row.reception_status || '').toLowerCase();
    case 'payment_status':
      return String(row.payment_status || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mult;
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const canRequestApproval = (statusRaw) => canRequestPoApproval(statusRaw);

const PurchaseOrdersTable = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  tableTitle = 'Órdenes de compra',
  addButtonLabel = 'Nueva OC',
  attached = false,
  hideChrome = false,
  onAdd,
  onRowClick,
  onBulkDelete,
  onBulkAssign,
  onDuplicate,
  onNewVersion,
  onFragment,
  onRequestApproval,
  onApprove,
  onReject,
  onMarkPaid,
  onInvoice,
  buyerOptions = [],
  paymentTermOptions = [],
}) => {
  const ui = useUIColors();
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');
  const [filterInvoicing, setFilterInvoicing] = useState('');
  const [filterReception, setFilterReception] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [sortColumn, setSortColumn] = useState('order_date');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignBuyer, setAssignBuyer] = useState('');
  const [assignStatus, setAssignStatus] = useState('');
  const [assignPaymentTerms, setAssignPaymentTerms] = useState('');
  const headerSelectRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete || onBulkAssign || onDuplicate || onNewVersion || onFragment);
  const showActions = Boolean(onRequestApproval || onApprove || onReject || onMarkPaid || onInvoice);
  const colCount =
    TABLE_COLUMNS.length + (showBulkChrome ? 1 : 0) + (showActions ? 1 : 0);

  const iconButtonStyle = (enabled, danger = false) => ({
    width: 28,
    height: 28,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${danger ? '#fecaca' : inputBorder}`,
    background: enabled ? (danger ? '#fef2f2' : inputBg) : isDarkMode ? '#27272a' : '#f5f5f4',
    color: enabled ? (danger ? '#dc2626' : text) : muted,
    cursor: enabled ? 'pointer' : 'not-allowed',
    opacity: enabled ? 1 : 0.45,
    padding: 0,
  });

  const enrichedRows = useMemo(
    () =>
      (rows || []).map((row) => {
        const lifecycle = resolvePoLifecycleStatuses(row);
        return { ...row, ...lifecycle };
      }),
    [rows]
  );

  const filteredRows = useMemo(() => {
    let list = enrichedRows;
    const tokens = searchQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.po_code,
          row.supplier_name,
          row.supplier_code,
          row.supplier_rut,
          row.supplier_contact_name,
          row.supplier_contact_email,
          row.supplier_contact_phone,
          row.buyer_name,
          row.document_type,
          row.payment_terms,
          row.status,
          row.workflow_status,
          row.invoicing_status,
          row.reception_status,
          row.payment_status,
          row.notes,
          row.comments,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterWorkflow) list = list.filter((row) => row.workflow_status === filterWorkflow);
    if (filterInvoicing) list = list.filter((row) => row.invoicing_status === filterInvoicing);
    if (filterReception) list = list.filter((row) => row.reception_status === filterReception);
    if (filterPayment) list = list.filter((row) => row.payment_status === filterPayment);
    return list;
  }, [
    enrichedRows,
    searchQuery,
    filterWorkflow,
    filterInvoicing,
    filterReception,
    filterPayment,
  ]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );
  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  useEffect(() => {
    if (selectedIds.length === 0) {
      setShowAssignModal(false);
      setAssignBuyer('');
      setAssignStatus('');
      setAssignPaymentTerms('');
    }
  }, [selectedIds.length]);

  const toggleSelectOne = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSortClick = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortColumn(key);
      setSortDir(
        ['order_date', 'subtotal', 'tax_amount', 'discounts', 'amount_due'].includes(key) ? 'desc' : 'asc'
      );
    }
  };

  const hasActiveFilters = Boolean(
    searchQuery.trim() || filterWorkflow || filterInvoicing || filterReception || filterPayment
  );
  const clearFilters = () => {
    setSearchQuery('');
    setFilterWorkflow('');
    setFilterInvoicing('');
    setFilterReception('');
    setFilterPayment('');
  };

  const clearSelection = () => setSelectedIds([]);

  const handleBulkDelete = async () => {
    if (!onBulkDelete || !selectedIds.length) return;
    setBulkPending(true);
    setBulkError('');
    try {
      const result = await onBulkDelete(selectedIds);
      if (result !== false) setSelectedIds([]);
    } catch (err) {
      setBulkError(err?.message || 'No se pudieron eliminar.');
    } finally {
      setBulkPending(false);
    }
  };

  const openAssignModal = () => {
    if (!onBulkAssign || !selectedIds.length) return;
    setAssignBuyer('');
    setAssignStatus('');
    setAssignPaymentTerms('');
    setShowAssignModal(true);
  };

  const handleConfirmAssign = async () => {
    if (!onBulkAssign || !selectedIds.length) return;
    const payload = { ids: selectedIds };
    if (assignBuyer) payload.buyer_name = assignBuyer;
    if (assignStatus) payload.status = assignStatus;
    if (assignPaymentTerms !== '') payload.payment_terms = assignPaymentTerms;
    if (!payload.buyer_name && !payload.status && !('payment_terms' in payload)) {
      window.alert('Selecciona al menos un campo para asignar (comprador, estado o condición de pago).');
      return;
    }
    setBulkPending(true);
    setBulkError('');
    try {
      await onBulkAssign(payload);
      setShowAssignModal(false);
      setSelectedIds([]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo asignar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleDuplicateSelected = async () => {
    if (!onDuplicate || !selectedIds.length) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Duplicar ${n} orden${n === 1 ? '' : 'es'} de compra? Se crear${n === 1 ? 'á' : 'án'} con código nuevo y estado Borrador.`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await onDuplicate(selectedIds);
      setSelectedIds([]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo duplicar.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleNewVersionSelected = async () => {
    if (!onNewVersion || !selectedIds.length) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Crear nueva versión de ${n} orden${n === 1 ? '' : 'es'} de compra?\n` +
        `Se usará el mismo código con sufijo -v1, -v2, … y la versión anterior quedará Anulada.`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await onNewVersion(selectedIds);
      setSelectedIds([]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo crear la nueva versión.');
    } finally {
      setBulkPending(false);
    }
  };

  const handleFragmentSelected = async () => {
    if (!onFragment || !selectedIds.length) return;
    const n = selectedIds.length;
    const ok = window.confirm(
      `¿Fragmentar ${n} orden${n === 1 ? '' : 'es'} de compra?\n` +
        `Se crear${n === 1 ? 'á' : 'án'} duplicado${n === 1 ? '' : 's'} con sufijo -01, -02, … ` +
        `y la OC original pasará a estado Para Reserva.`
    );
    if (!ok) return;
    setBulkPending(true);
    setBulkError('');
    try {
      await onFragment(selectedIds);
      setSelectedIds([]);
    } catch (err) {
      window.alert(err?.message || 'No se pudo fragmentar.');
    } finally {
      setBulkPending(false);
    }
  };

  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} registros`
        : `${rows.length} registros`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '140px',
    cursor: 'pointer',
    outline: 'none',
  };

  const SortIcon = ({ columnKey }) => {
    if (sortColumn !== columnKey) return <ArrowUpDown size={12} color={muted} />;
    return sortDir === 'asc' ? <ArrowUp size={12} color={text} /> : <ArrowDown size={12} color={text} />;
  };

  return (
    <div
      style={
        hideChrome
          ? { background: bg }
          : {
              border: `1px solid ${border}`,
              borderTop: attached ? 'none' : `1px solid ${border}`,
              borderRadius: attached ? '0 0 12px 12px' : '12px',
              overflow: 'hidden',
              background: bg,
            }
      }
    >
      {hideChrome ? null : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: `1px solid ${border}`,
            background: headerBg,
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>{tableTitle}</h3>
            <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
          </div>
          {onAdd ? (
            <button
              type="button"
              onClick={onAdd}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '36px',
                padding: '0 14px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontWeight: 600,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              <Plus size={16} /> {addButtonLabel}
            </button>
          ) : null}
        </div>
      )}

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Código, proveedor, RUT, contacto…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, cursor: 'text' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '130px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Estado</span>
          <select value={filterWorkflow} onChange={(e) => setFilterWorkflow(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {PO_WORKFLOW_STATUS_ORDER.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Facturación
          </span>
          <select value={filterInvoicing} onChange={(e) => setFilterInvoicing(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {PO_INVOICING_STATUS_ORDER.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Recepción
          </span>
          <select value={filterReception} onChange={(e) => setFilterReception(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {PO_RECEPTION_STATUS_ORDER.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '130px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Pago</span>
          <select value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)} style={selectStyle}>
            <option value="">Todos</option>
            {PO_PAYMENT_STATUS_ORDER.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            Limpiar
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: isDarkMode ? '#1c1917' : '#fafaf9',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', color: text, fontWeight: 600 }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          {onBulkDelete ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleBulkDelete}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#dc2626',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              <Trash2 size={14} /> Eliminar
            </button>
          ) : null}
          {onBulkAssign ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={openAssignModal}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              <UserPlus size={14} /> Asignar
            </button>
          ) : null}
          {onDuplicate ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleDuplicateSelected}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#0f766e',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              <Copy size={14} /> Duplicar
            </button>
          ) : null}
          {onNewVersion ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleNewVersionSelected}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#7c3aed',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              <GitBranch size={14} /> Nueva versión
            </button>
          ) : null}
          {onFragment ? (
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleFragmentSelected}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                height: '32px',
                padding: '0 12px',
                borderRadius: '6px',
                border: 'none',
                background: '#c2410c',
                color: '#fff',
                fontWeight: 700,
                fontSize: '12px',
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              <Scissors size={14} /> Fragmentar
            </button>
          ) : null}
          <button
            type="button"
            disabled={bulkPending}
            onClick={clearSelection}
            style={{
              height: '32px',
              padding: '0 12px',
              borderRadius: '6px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: bulkPending ? 'not-allowed' : 'pointer',
              marginLeft: 'auto',
            }}
          >
            Quitar selección
          </button>
          {bulkError ? <span style={{ fontSize: '12px', color: '#dc2626' }}>{bulkError}</span> : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1180px' }}>
          <thead>
            <tr style={{ background: headerBg, borderBottom: `1px solid ${border}` }}>
              {showBulkChrome ? (
                <th style={{ padding: `${thPadV}px ${padH}px`, width: 36, textAlign: 'center' }}>
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: col.align,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                  onClick={() => handleSortClick(col.sortKey)}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {col.key}
                    <SortIcon columnKey={col.sortKey} />
                  </span>
                </th>
              ))}
              {showActions ? (
                <th
                  style={{
                    padding: `${thPadV}px ${padH}px`,
                    textAlign: 'right',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Acciones
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  Cargando órdenes…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: '#dc2626' }}>
                  {error}
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 24, textAlign: 'center', color: muted }}>
                  No hay órdenes de compra para mostrar.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const lifecycle = {
                  workflow_status: row.workflow_status,
                  invoicing_status: row.invoicing_status,
                  reception_status: row.reception_status,
                  payment_status: row.payment_status,
                };
                const st = statusPresentation(lifecycle.workflow_status, isDarkMode);
                const invoicing = getPoInvoicingStatusPresentation(lifecycle.invoicing_status, isDarkMode);
                const reception = getPoReceptionStatusPresentation(lifecycle.reception_status, isDarkMode);
                const payment = getPoPaymentStatusPresentation(lifecycle.payment_status, isDarkMode);
                const doc = documentTypePresentation(row.document_type, isDarkMode);
                const selected = selectedIds.includes(Number(row.id));
                const currency = String(row.currency || 'CLP').toUpperCase();
                const netClp = Number(row.subtotal || 0);
                const netOriginal =
                  row.subtotal_original != null ? Number(row.subtotal_original) : netClp;
                const advance = Number(row.advance_amount || 0);
                const withholdings = Number(row.withholdings_amount || 0);
                const discounts = advance + withholdings;
                const amountDue =
                  row.amount_due != null
                    ? Number(row.amount_due)
                    : Number(row.total_amount || 0) - advance - withholdings;
                const contactMeta = [row.supplier_contact_email, row.supplier_contact_phone]
                  .filter(Boolean)
                  .join(' · ');
                const canMarkPaid =
                  lifecycle.payment_status === 'Por Pagar' ||
                  lifecycle.payment_status === 'Vencido' ||
                  lifecycle.payment_status === 'Pago Parcial' ||
                  isPoAwaitingPaymentStatus(row.status);

                return (
                  <tr
                    key={row.id}
                    style={{ borderBottom: `1px solid ${border}`, background: selected ? rowHover : bg }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected ? rowHover : bg;
                    }}
                  >
                    {showBulkChrome ? (
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleSelectOne(row.id)}
                          aria-label={`Seleccionar ${row.po_code || row.id}`}
                        />
                      </td>
                    ) : null}

                    <td style={{ padding: 0, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 40 }}>
                        <div
                          style={{
                            width: 8,
                            flexShrink: 0,
                            background: st.bar,
                            borderRadius: '0 3px 3px 0',
                          }}
                          title={st.label}
                        />
                        <div style={{ padding: `${padV}px ${padH}px`, display: 'flex', alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => onRowClick?.(row)}
                            style={{
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                              cursor: 'pointer',
                              color: '#2563eb',
                              fontWeight: 700,
                              fontSize: 12,
                              lineHeight: 1.2,
                              textAlign: 'left',
                            }}
                          >
                            {row.po_code || '—'}
                          </button>
                        </div>
                      </div>
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
                      <Stacked
                        top={formatDate(row.order_date)}
                        bottom={row.buyer_name || row.created_by || ''}
                        topColor={text}
                        mutedColor={muted}
                        topWeight={600}
                      />
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: 200 }}>
                      <Stacked
                        top={row.supplier_name}
                        bottom={row.supplier_rut || ''}
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', maxWidth: 180 }}>
                      <Stacked
                        top={row.supplier_contact_name}
                        bottom={contactMeta}
                        topColor={text}
                        mutedColor={muted}
                        topWeight={600}
                      />
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle' }}>
                      {doc ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            background: doc.bg,
                            color: doc.color,
                            border: `1px solid ${doc.border}`,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {doc.label}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: muted }}>—</span>
                      )}
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', fontSize: 12, color: text }}>
                      {row.payment_terms || '—'}
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'right' }}>
                      <Stacked
                        top={formatMoney(netClp, 'CLP')}
                        bottom={
                          currency !== 'CLP' || Number(row.exchange_rate || 1) !== 1
                            ? `${formatMoney(netOriginal, currency)}`
                            : ''
                        }
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>

                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 600,
                        color: text,
                      }}
                    >
                      {formatMoney(row.tax_amount, 'CLP')}
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'right' }}>
                      <Stacked
                        top={formatMoney(discounts, 'CLP')}
                        bottom={
                          discounts > 0
                            ? [
                                advance > 0 ? `Ant. ${formatMoney(advance, 'CLP')}` : null,
                                withholdings > 0 ? `Ret. ${formatMoney(withholdings, 'CLP')}` : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')
                            : ''
                        }
                        topColor={text}
                        mutedColor={muted}
                      />
                    </td>

                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        verticalAlign: 'middle',
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 700,
                        color: text,
                      }}
                    >
                      {formatMoney(amountDue, 'CLP')}
                    </td>

                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <StatusBadge presentation={st} />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <StatusBadge presentation={invoicing} />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <StatusBadge presentation={reception} />
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <StatusBadge presentation={payment} />
                    </td>
                    {showActions ? (
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          textAlign: 'right',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            flexWrap: 'nowrap',
                            gap: 4,
                            justifyContent: 'flex-end',
                          }}
                        >
                          {onRequestApproval ? (
                            <button
                              type="button"
                              style={iconButtonStyle(canRequestApproval(row.status))}
                              disabled={!canRequestApproval(row.status)}
                              title={
                                canRequestApproval(row.status)
                                  ? 'Solicitar aprobación'
                                  : 'Disponible solo cuando la OC está en Borrador'
                              }
                              aria-label="Solicitar aprobación"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canRequestApproval(row.status)) return;
                                onRequestApproval(row);
                              }}
                            >
                              <BadgeCheck size={14} />
                            </button>
                          ) : null}
                          {onApprove ? (
                            <button
                              type="button"
                              style={iconButtonStyle(
                                lifecycle.workflow_status === 'Por Aprobar' ||
                                  isPendingPoApprovalStatus(row.status)
                              )}
                              disabled={
                                !(
                                  lifecycle.workflow_status === 'Por Aprobar' ||
                                  isPendingPoApprovalStatus(row.status)
                                )
                              }
                              title={
                                lifecycle.workflow_status === 'Por Aprobar' ||
                                isPendingPoApprovalStatus(row.status)
                                  ? 'Aprobar orden'
                                  : 'Disponible solo en Por Aprobar'
                              }
                              aria-label="Aprobar orden"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  !(
                                    lifecycle.workflow_status === 'Por Aprobar' ||
                                    isPendingPoApprovalStatus(row.status)
                                  )
                                ) {
                                  return;
                                }
                                onApprove(row);
                              }}
                            >
                              <Check size={14} />
                            </button>
                          ) : null}
                          {onReject ? (
                            <button
                              type="button"
                              style={iconButtonStyle(
                                lifecycle.workflow_status === 'Por Aprobar' ||
                                  isPendingPoApprovalStatus(row.status),
                                true
                              )}
                              disabled={
                                !(
                                  lifecycle.workflow_status === 'Por Aprobar' ||
                                  isPendingPoApprovalStatus(row.status)
                                )
                              }
                              title={
                                lifecycle.workflow_status === 'Por Aprobar' ||
                                isPendingPoApprovalStatus(row.status)
                                  ? 'Rechazar aprobación'
                                  : 'Disponible solo en Por Aprobar'
                              }
                              aria-label="Rechazar aprobación"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  !(
                                    lifecycle.workflow_status === 'Por Aprobar' ||
                                    isPendingPoApprovalStatus(row.status)
                                  )
                                ) {
                                  return;
                                }
                                onReject(row);
                              }}
                            >
                              <X size={14} />
                            </button>
                          ) : null}
                          {onMarkPaid ? (
                            <button
                              type="button"
                              style={iconButtonStyle(canMarkPaid)}
                              disabled={!canMarkPaid}
                              title={canMarkPaid ? 'Marcar como pagada' : 'Disponible solo cuando está por pagar'}
                              aria-label="Marcar como pagada"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!canMarkPaid) return;
                                onMarkPaid(row);
                              }}
                            >
                              <Banknote size={14} />
                            </button>
                          ) : null}
                          {onInvoice ? (
                            <button
                              type="button"
                              style={iconButtonStyle(true)}
                              title="Facturación"
                              aria-label="Facturación"
                              onClick={(e) => {
                                e.stopPropagation();
                                onInvoice(row);
                              }}
                            >
                              <Receipt size={14} />
                            </button>
                          ) : null}
                        </span>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={showAssignModal}
        onClose={() => {
          if (!bulkPending) setShowAssignModal(false);
        }}
        title="Asignar a órdenes de compra"
        subtitle={
          selectedIds.length === 1
            ? 'Asigna comprador, estado o condición de pago a la OC seleccionada. Deja en vacío lo que no quieras cambiar.'
            : `Asigna los mismos valores a ${selectedIds.length} órdenes. Deja en vacío lo que no quieras cambiar.`
        }
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Comprador</span>
            <select
              value={assignBuyer}
              onChange={(e) => setAssignBuyer(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {(buyerOptions || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={assignStatus}
              onChange={(e) => setAssignStatus(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {PO_STATUS_ORDER.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Condición de pago</span>
            <select
              value={assignPaymentTerms}
              onChange={(e) => setAssignPaymentTerms(e.target.value)}
              disabled={bulkPending}
              style={{ ...selectStyle, height: '40px' }}
            >
              <option value="">Sin cambiar</option>
              {(paymentTermOptions || [])
                .filter((o) => o.value)
                .map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
            </select>
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={bulkPending}
              onClick={() => setShowAssignModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={bulkPending}
              onClick={handleConfirmAssign}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: bulkPending ? 'not-allowed' : 'pointer',
                opacity: bulkPending ? 0.75 : 1,
              }}
            >
              {bulkPending ? 'Guardando…' : 'Aplicar'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PurchaseOrdersTable;
